import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { tokens } from '@/design';
import { LayoutElementChrome } from './LayoutElementChrome';
import { LayoutGraphic } from './LayoutGraphic';
import { LayoutGraphicDrawingOverlay, type DrawnLayoutGraphic } from './LayoutGraphicDrawingOverlay';
import { LayoutGraphicVertexOverlay } from './LayoutGraphicVertexOverlay';
import { LayoutLegend } from './LayoutLegend';
import { LayoutNorthArrow } from './LayoutNorthArrow';
import {
  northArrowRotationForElement,
  northArrowVisibleAtScale,
} from './northArrowOrientation';
import { LayoutScaleBar } from './LayoutScaleBar';
import { LayoutTableFrame } from './LayoutTableFrame';
import { LayoutPictureGraphic } from './LayoutPictureGraphic';
import { LayoutPicturePlacementOverlay } from './LayoutPicturePlacementOverlay';
import type { PlacedLayoutPicture } from './LayoutPicturePlacementOverlay';
import { effectiveScaleBarDenominator } from './scaleBarGeometry';
import type { ResizeHandleId } from './elementGeometry';
import { pointInRect } from './elementGeometry';
import { legendItemsForLayers } from './legendLayerItems';
import { MM_PER_INCH, mmToScreenPx, pageSizePx, screenPxToMm } from './pageUnits';
import type { Layer } from '@geosoft/viewer-sdk';
import type {
  LayoutDocument,
  LayoutFrameEngine,
  LayoutGraphicGeometry,
  LayoutGraphicType,
  LayoutPictureSource,
  LegendLayerItem,
  MapFrameCamera,
  MapFrameElement,
  TableFrameRuntimeData,
} from './layoutTypes';

const LayoutMapFrame = lazy(async () => {
  const mod = await import('./LayoutMapFrame');
  return { default: mod.LayoutMapFrame };
});

export interface LayoutCanvasProps {
  readonly document: LayoutDocument;
  readonly selectedId: string | null;
  readonly selectedIds: ReadonlySet<string>;
  readonly pageZoom: number;
  readonly frameLayers: readonly Layer[];
  readonly frameEngine: LayoutFrameEngine;
  readonly legendLayers: readonly LegendLayerItem[];
  readonly visibleLayerIds: ReadonlySet<string>;
  readonly tableDataByLayer: Readonly<Record<string, TableFrameRuntimeData>>;
  readonly onSelect: (id: string | null, additive?: boolean) => void;
  readonly onMove: (id: string, dxMm: number, dyMm: number) => void;
  readonly onResize: (id: string, handle: ResizeHandleId, pointerMm: { x: number; y: number }) => void;
  readonly onRotate: (id: string, degrees: number) => void;
  readonly onGestureStart: () => void;
  readonly onGestureEnd: () => void;
  readonly editGraphicId: string | null;
  readonly onVertexGeometry: (id: string, geometry: LayoutGraphicGeometry) => void;
  readonly onFinishVertexEdit: () => void;
  readonly onCancelVertexEdit: () => void;
  readonly onTextCommit: (id: string, text: string) => void;
  readonly onMapFrameCamera: (frameId: string, camera: MapFrameCamera) => void;
  readonly onMapCanvasReady?: (canvas: HTMLCanvasElement | null) => void;
  readonly drawingTool: LayoutGraphicType | null;
  readonly onDrawingComplete: (graphic: DrawnLayoutGraphic) => void;
  readonly onDrawingCancel: () => void;
  readonly pendingPicture: LayoutPictureSource | null;
  readonly onPicturePlaced: (placement: PlacedLayoutPicture) => void;
  readonly onPicturePlaceCancel: () => void;
}

function rulerTicks(
  lengthMm: number,
  pageZoom: number,
  units: LayoutDocument['page']['units'],
): { readonly mm: number; readonly label: string }[] {
  const stepMm = units === 'in' ? MM_PER_INCH : pageZoom < 0.5 ? 20 : 10;
  const ticks: { mm: number; label: string }[] = [];
  for (let mm = 0; mm <= lengthMm + 0.01; mm += stepMm) {
    ticks.push({
      mm,
      label: units === 'in' ? String(Math.round(mm / MM_PER_INCH)) : String(mm),
    });
  }
  return ticks;
}

export function LayoutCanvas({
  document,
  selectedId,
  selectedIds,
  pageZoom,
  frameLayers,
  frameEngine,
  legendLayers,
  visibleLayerIds,
  tableDataByLayer,
  onSelect,
  onMove,
  onResize,
  onRotate,
  onGestureStart,
  onGestureEnd,
  editGraphicId,
  onVertexGeometry,
  onFinishVertexEdit,
  onCancelVertexEdit,
  onTextCommit,
  onMapFrameCamera,
  onMapCanvasReady,
  drawingTool,
  onDrawingComplete,
  onDrawingCancel,
  pendingPicture,
  onPicturePlaced,
  onPicturePlaceCancel,
}: LayoutCanvasProps) {
  const pasteboardRef = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState({ x: 48, y: 48 });
  const [textEdit, setTextEdit] = useState<{ id: string; value: string } | null>(null);
  const dragRef = useRef<{
    mode: 'move' | 'resize' | 'rotate' | 'pan';
    id?: string;
    handle?: ResizeHandleId;
    lastX: number;
    lastY: number;
    centerX?: number;
    centerY?: number;
  } | null>(null);

  const pagePx = pageSizePx(document.page.widthMm, document.page.heightMm, pageZoom);
  const ruler = 24;

  const pointerToPageMm = useCallback(
    (clientX: number, clientY: number) => {
      const board = pasteboardRef.current;
      if (!board) return { x: 0, y: 0 };
      const bounds = board.getBoundingClientRect();
      const xPx = clientX - bounds.left - offset.x;
      const yPx = clientY - bounds.top - offset.y;
      return { x: screenPxToMm(xPx, pageZoom), y: screenPxToMm(yPx, pageZoom) };
    },
    [offset.x, offset.y, pageZoom],
  );

  useEffect(() => {
    const onMovePtr = (event: globalThis.PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      if (drag.mode === 'pan') {
        setOffset((prev) => ({
          x: prev.x + (event.clientX - drag.lastX),
          y: prev.y + (event.clientY - drag.lastY),
        }));
        drag.lastX = event.clientX;
        drag.lastY = event.clientY;
        return;
      }
      if (drag.mode === 'move' && drag.id) {
        const dx = screenPxToMm(event.clientX - drag.lastX, pageZoom);
        const dy = screenPxToMm(event.clientY - drag.lastY, pageZoom);
        drag.lastX = event.clientX;
        drag.lastY = event.clientY;
        onMove(drag.id, dx, dy);
        return;
      }
      if (drag.mode === 'resize' && drag.id && drag.handle) {
        onResize(drag.id, drag.handle, pointerToPageMm(event.clientX, event.clientY));
        return;
      }
      if (drag.mode === 'rotate' && drag.id && drag.centerX !== undefined && drag.centerY !== undefined) {
        const previous = Math.atan2(drag.lastY - drag.centerY, drag.lastX - drag.centerX);
        const next = Math.atan2(event.clientY - drag.centerY, event.clientX - drag.centerX);
        onRotate(drag.id, ((next - previous) * 180) / Math.PI);
        drag.lastX = event.clientX;
        drag.lastY = event.clientY;
      }
    };
    const onUp = () => {
      if (dragRef.current && dragRef.current.mode !== 'pan') onGestureEnd();
      dragRef.current = null;
    };
    window.addEventListener('pointermove', onMovePtr);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMovePtr);
      window.removeEventListener('pointerup', onUp);
    };
  }, [onGestureEnd, onMove, onResize, onRotate, pageZoom, pointerToPageMm]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'F2' || !selectedId || textEdit) return;
      const active = globalThis.document.activeElement;
      if (!pasteboardRef.current?.contains(active)) return;
      const graphic = document.elements.find((element) =>
        element.type === 'graphic' && element.id === selectedId && element.category === 'text');
      if (!graphic || graphic.type !== 'graphic' || graphic.category !== 'text') return;
      event.preventDefault();
      setTextEdit({ id: graphic.id, value: graphic.text.text });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [document.elements, selectedId, textEdit]);

  const frames = document.elements.filter((el): el is MapFrameElement => el.type === 'mapFrame');

  return (
    <div
      data-testid="layout-canvas"
      style={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        overflow: 'hidden',
        background: tokens.color.surface[2],
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        aria-hidden
        style={{
          height: ruler,
          marginLeft: ruler,
          background: tokens.color.surface[1],
          borderBottom: tokens.border.thin,
          position: 'relative',
          color: tokens.color.ink.muted,
          fontFamily: tokens.font.family.mono,
          fontSize: 9,
        }}
      >
        {rulerTicks(document.page.widthMm, pageZoom, document.page.units).map((tick) => (
          <span
            key={`hx-${tick.mm}`}
            style={{
              position: 'absolute',
              left: offset.x + mmToScreenPx(tick.mm, pageZoom),
              top: 0,
              borderLeft: tokens.border.thin,
              height: '100%',
              paddingLeft: 2,
            }}
          >
            {tick.label}
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <div
          aria-hidden
          style={{
            width: ruler,
            background: tokens.color.surface[1],
            borderRight: tokens.border.thin,
            position: 'relative',
            color: tokens.color.ink.muted,
            fontFamily: tokens.font.family.mono,
            fontSize: 9,
          }}
        >
          {rulerTicks(document.page.heightMm, pageZoom, document.page.units).map((tick) => (
            <span
              key={`vy-${tick.mm}`}
              style={{
                position: 'absolute',
                top: offset.y + mmToScreenPx(tick.mm, pageZoom),
                left: 0,
                borderTop: tokens.border.thin,
                width: '100%',
                paddingLeft: 2,
              }}
            >
              {tick.label}
            </span>
          ))}
        </div>
        <div
          ref={pasteboardRef}
          data-testid="layout-pasteboard"
          tabIndex={0}
          onPointerDown={(event) => {
            event.currentTarget.focus({ preventScroll: true });
            if (event.button === 1) {
              dragRef.current = {
                mode: 'pan',
                lastX: event.clientX,
                lastY: event.clientY,
              };
              return;
            }
            const mm = pointerToPageMm(event.clientX, event.clientY);
            const hit = [...document.elements].reverse().find((el) =>
              pointInRect(mm.x, mm.y, el.rect),
            );
            onSelect(hit?.id ?? null, event.ctrlKey || event.metaKey || event.shiftKey);
            if (!hit) {
              dragRef.current = {
                mode: 'pan',
                lastX: event.clientX,
                lastY: event.clientY,
              };
            }
          }}
          style={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            cursor: 'grab',
          }}
        >
          <div
            data-testid="layout-page"
            style={{
              position: 'absolute',
              left: offset.x,
              top: offset.y,
              width: pagePx.width,
              height: pagePx.height,
              background: tokens.color.canvas.light,
              boxShadow: tokens.elevation[2],
            }}
          >
            {document.elements.map((el) => {
              if (el.type === 'mapFrame') {
                return (
                  <LayoutElementChrome
                    key={el.id}
                    rect={el.rect}
                    pageZoom={pageZoom}
                    selected={selectedIds.has(el.id)}
                    label="Map frame"
                    data-testid={`layout-element-${el.id}`}
                    moveFromHandleOnly
                    onSelect={(event) => onSelect(el.id, Boolean(event?.ctrlKey || event?.metaKey || event?.shiftKey))}
                    onMovePointerDown={(event) => {
                      dragRef.current = {
                        mode: 'move',
                        id: el.id,
                        lastX: event.clientX,
                        lastY: event.clientY,
                      };
                    }}
                    onResizePointerDown={(handle, event) => {
                      dragRef.current = {
                        mode: 'resize',
                        id: el.id,
                        handle,
                        lastX: event.clientX,
                        lastY: event.clientY,
                      };
                    }}
                  >
                    <Suspense fallback={null}>
                    <LayoutMapFrame
                      layers={frameLayers}
                      frameEngine={frameEngine}
                      camera={el.camera}
                      onCameraChange={(camera) => onMapFrameCamera(el.id, camera)}
                      onCanvasReady={onMapCanvasReady}
                    />
                    </Suspense>
                  </LayoutElementChrome>
                );
              }
              if (el.type === 'graphic') {
                return (
                  <LayoutElementChrome
                    key={el.id}
                    rect={el.rect}
                    pageZoom={pageZoom}
                    selected={selectedIds.has(el.id)}
                    label={el.name}
                    locked={el.locked}
                    data-testid={`layout-element-${el.id}`}
                    onSelect={(event) => onSelect(el.id, Boolean(event?.ctrlKey || event?.metaKey || event?.shiftKey))}
                    onMovePointerDown={(event) => {
                      onGestureStart();
                      dragRef.current = { mode: 'move', id: el.id, lastX: event.clientX, lastY: event.clientY };
                    }}
                    onResizePointerDown={(handle, event) => {
                      onGestureStart();
                      dragRef.current = { mode: 'resize', id: el.id, handle, lastX: event.clientX, lastY: event.clientY };
                    }}
                    onRotatePointerDown={(event) => {
                      const board = pasteboardRef.current?.getBoundingClientRect();
                      if (!board) return;
                      onGestureStart();
                      dragRef.current = {
                        mode: 'rotate',
                        id: el.id,
                        lastX: event.clientX,
                        lastY: event.clientY,
                        centerX: board.left + offset.x + mmToScreenPx(el.rect.x + el.rect.width / 2, pageZoom),
                        centerY: board.top + offset.y + mmToScreenPx(el.rect.y + el.rect.height / 2, pageZoom),
                      };
                    }}
                    onDoubleClick={el.category === 'text' ? () =>
                      setTextEdit({ id: el.id, value: el.text.text }) : undefined}
                  >
                    <LayoutGraphic element={el} />
                    {textEdit?.id === el.id && el.category === 'text' ? (
                      <textarea
                        autoFocus
                        aria-label="Edit graphic text"
                        value={textEdit.value}
                        onPointerDown={(event) => event.stopPropagation()}
                        onChange={(event) => setTextEdit({ ...textEdit, value: event.target.value })}
                        onBlur={() => {
                          onTextCommit(el.id, textEdit.value);
                          setTextEdit(null);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Escape') {
                            event.preventDefault();
                            setTextEdit(null);
                          } else if ((event.ctrlKey && event.key === 'Enter') || event.key === 'F2') {
                            event.preventDefault();
                            onTextCommit(el.id, textEdit.value);
                            setTextEdit(null);
                          }
                        }}
                        style={{ position: 'absolute', inset: 0, zIndex: 5, resize: 'none' }}
                      />
                    ) : null}
                  </LayoutElementChrome>
                );
              }
              if (el.type === 'picture') {
                return (
                  <LayoutElementChrome
                    key={el.id}
                    rect={el.rect}
                    pageZoom={pageZoom}
                    selected={selectedIds.has(el.id)}
                    label={el.name}
                    locked={el.locked}
                    data-testid={`layout-element-${el.id}`}
                    onSelect={(event) => onSelect(el.id, Boolean(event?.ctrlKey || event?.metaKey || event?.shiftKey))}
                    onMovePointerDown={(event) => {
                      onGestureStart();
                      dragRef.current = { mode: 'move', id: el.id, lastX: event.clientX, lastY: event.clientY };
                    }}
                    onResizePointerDown={(handle, event) => {
                      onGestureStart();
                      dragRef.current = { mode: 'resize', id: el.id, handle, lastX: event.clientX, lastY: event.clientY };
                    }}
                    onRotatePointerDown={(event) => {
                      const board = pasteboardRef.current?.getBoundingClientRect();
                      if (!board) return;
                      onGestureStart();
                      dragRef.current = {
                        mode: 'rotate',
                        id: el.id,
                        lastX: event.clientX,
                        lastY: event.clientY,
                        centerX: board.left + offset.x + mmToScreenPx(el.rect.x + el.rect.width / 2, pageZoom),
                        centerY: board.top + offset.y + mmToScreenPx(el.rect.y + el.rect.height / 2, pageZoom),
                      };
                    }}
                  >
                    <LayoutPictureGraphic element={el} />
                  </LayoutElementChrome>
                );
              }
              const frame =
                frames.find((f) => f.id === el.mapFrameId) ?? frames[0] ?? null;
              const bearing = frame?.camera.bearing ?? 0;
              const scaleDenominator = frame?.camera.scaleDenominator ?? null;
              return (
                <LayoutElementChrome
                  key={el.id}
                  rect={el.rect}
                  pageZoom={pageZoom}
                  selected={selectedIds.has(el.id)}
                  label={el.name}
                  locked={
                    (
                      el.type === 'northArrow'
                      || el.type === 'scaleBar'
                      || el.type === 'legend'
                      || el.type === 'tableFrame'
                    )
                    && el.locked
                  }
                  lockHorizontalResize={
                    el.type === 'scaleBar'
                    && (
                      el.fittingStrategy === 'adjustWidth'
                      || el.fittingStrategy === 'fixedBarWidth'
                    )
                  }
                  data-testid={`layout-element-${el.id}`}
                  onSelect={(event) => onSelect(el.id, Boolean(event?.ctrlKey || event?.metaKey || event?.shiftKey))}
                  onMovePointerDown={(event) => {
                    dragRef.current = {
                      mode: 'move',
                      id: el.id,
                      lastX: event.clientX,
                      lastY: event.clientY,
                    };
                  }}
                  onResizePointerDown={(handle, event) => {
                    dragRef.current = {
                      mode: 'resize',
                      id: el.id,
                      handle,
                      lastX: event.clientX,
                      lastY: event.clientY,
                    };
                  }}
                >
                  {el.type === 'northArrow' ? (
                    northArrowVisibleAtScale(el, scaleDenominator) ? (
                      <LayoutNorthArrow
                        styleId={el.styleId}
                        bearingDeg={bearing}
                        rotationDeg={northArrowRotationForElement(el, frame?.camera ?? {
                          center: [0, 0],
                          zoom: 0,
                          bearing,
                          scaleDenominator,
                        })}
                        color={el.symbol?.color}
                        marker={el.symbol?.marker}
                        halo={el.symbol?.halo}
                      />
                    ) : null
                  ) : null}
                  {el.type === 'scaleBar' ? (
                    el.visible ? (
                      <LayoutScaleBar
                        styleId={el.styleId}
                        units={el.units}
                        scaleDenominator={
                          frame
                            ? effectiveScaleBarDenominator(el, frame.camera)
                            : scaleDenominator
                        }
                        maxWidthMm={el.rect.width}
                        heightMm={el.rect.height}
                        element={el}
                      />
                    ) : null
                  ) : null}
                  {el.type === 'legend' ? (
                    <LayoutLegend
                      legend={el}
                      items={legendItemsForLayers(
                        legendLayers,
                        visibleLayerIds,
                        el.layerIds,
                      )}
                    />
                  ) : null}
                  {el.type === 'tableFrame' ? (
                    <LayoutTableFrame
                      element={el}
                      data={
                        el.sourceLayerId
                          ? tableDataByLayer[el.sourceLayerId] ?? null
                          : null
                      }
                    />
                  ) : null}
                </LayoutElementChrome>
              );
            })}
            {editGraphicId ? (() => {
              const graphic = document.elements.find((element) =>
                element.type === 'graphic' && element.id === editGraphicId);
              return graphic?.type === 'graphic' ? (
                <LayoutGraphicVertexOverlay
                  graphic={graphic}
                  pageWidthMm={document.page.widthMm}
                  pageHeightMm={document.page.heightMm}
                  onGeometryChange={(geometry) => onVertexGeometry(graphic.id, geometry)}
                  onFinish={onFinishVertexEdit}
                  onCancel={onCancelVertexEdit}
                />
              ) : null;
            })() : null}
            {drawingTool ? (
              <LayoutGraphicDrawingOverlay
                tool={drawingTool}
                pageWidthMm={document.page.widthMm}
                pageHeightMm={document.page.heightMm}
                onComplete={onDrawingComplete}
                onCancel={onDrawingCancel}
              />
            ) : null}
            {pendingPicture ? (
              <LayoutPicturePlacementOverlay
                source={pendingPicture}
                pageWidthMm={document.page.widthMm}
                pageHeightMm={document.page.heightMm}
                onComplete={onPicturePlaced}
                onCancel={onPicturePlaceCancel}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
